import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router';

import { HubErrorBoundary } from '@adflow/frontend/ui-common';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import { theme } from '@adflow/frontend/ui-theme';
import { AbilityProvider, useRefreshUser } from '@adflow/frontend/util-auth';
import {
	useWebsocketListener,
	useWebsockets,
} from '@adflow/frontend/util-websockets';
import { ServerToClientEvents } from '@adflow/shared/util-websockets';

import { Header } from './Header/Header';
import { RequireLogin } from './Login/RequireLogin';

const styles = {
	app: css({
		width: '100%',
		height: '100%',
		overflow: 'hidden',

		display: 'grid',
		gridTemplateRows: 'auto 1fr',
		gridTemplateColumns: '100%',
		gridTemplateAreas: "'header' 'main'",
		backgroundColor: theme.colors.background.default,
	}),
	header: css({
		gridArea: 'header',
	}),
	mainContainer: css({
		gridArea: 'main',
		/* Each page component is responsible for its own scrolling. */
		overflow: 'hidden',
		height: '100%',
	}),
};

function SessionListener() {
	const refreshUser = useRefreshUser();

	useWebsocketListener(ServerToClientEvents.SessionMutated, () => {
		void refreshUser();
	});

	return null;
}

function WSKeeper() {
	const wsClient = useWebsockets();

	useEffect(() => {
		// ensure we are connected to the WS client
		wsClient?.connect();
	}, [wsClient]);

	return null;
}

function App() {
	return (
		<RequireLogin>
			<AbilityProvider>
				<div css={styles.app}>
					<Header outerStyle={styles.header} />
					<div css={styles.mainContainer}>
						<HubErrorBoundary>
							<Suspense fallback={<LoadingIndicator />}>
								<Outlet />
							</Suspense>
						</HubErrorBoundary>
					</div>
				</div>
				<SessionListener />
				<WSKeeper />
			</AbilityProvider>
		</RequireLogin>
	);
}

export default Sentry.withProfiler(App);
